import React, { useEffect, useState } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
} from "@tanstack/react-table";
import { observer } from "mobx-react-lite";
import { getColumns } from "./DataColumns";
import { useTranslation } from "react-i18next";

interface DataTableProps {
    data: any[];
}

export const DataTable = observer(({ data }: DataTableProps) => {
    // const [tableData, setTableData] = useState(data || []);
    const [tableData, setTableData] = useState<any[]>([]);
    const [editRowId, setEditRowId] = useState<number | null>(null);
    const [editRowValues, setEditRowValues] = useState<any>({});
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const { t } = useTranslation("data_table");

    useEffect(() => {
        setTableData(data || []);
    }, [data]);

    const columns = getColumns({
        tableData,
        setTableData,
        editRowId,
        setEditRowId,
        editRowValues,
        setEditRowValues,
        selectedRows,
        setSelectedRows,
        t,
    });

    const [globalFilter, setGlobalFilter] = useState("");
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);

    const table = useReactTable({
        data: tableData,
        columns,
        state: { sorting, columnFilters, globalFilter },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        globalFilterFn: "includesString",
    });

    return (
        <div className=" max-w-full">
            <h2 className="text-2xl font-semibold mb-4">{t("request_type")}</h2>

            <div className="flex gap-2 items-center mb-4">
                <input
                    value={globalFilter ?? ""}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={t("search_columns")}
                    className="border rounded p-2 flex-1"
                />
                <div className="text-sm text-gray-600">
                    {t("row")} {table.getRowModel().rows.length}
                </div>
            </div>

            <div className="overflow-x-auto border rounded">
                <table className="min-w-full divide-y">
                    <thead className="bg-gray-50">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="px-3 py-2 text-left text-base"
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef
                                                      .header,
                                                  header.getContext()
                                              )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody className="bg-white divide-y">
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id} className="hover:bg-gray-50">
                                {row.getVisibleCells().map((cell) => (
                                    <td
                                        key={cell.id}
                                        className="px-3 py-2 text-sm"
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between gap-2 mt-4">
                <div className="flex items-center gap-2">
                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"<<"}
                    </button>
                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"<"}
                    </button>
                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        {">"}
                    </button>
                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() =>
                            table.setPageIndex(table.getPageCount() - 1)
                        }
                        disabled={!table.getCanNextPage()}
                    >
                        {">>"}
                    </button>
                </div>
                <div className="flex items-center gap-2 text-sm">
                    <span>{t("page")}</span>
                    <strong>
                        {table.getState().pagination.pageIndex + 1} {t("of")}{" "}
                        {table.getPageCount()}
                    </strong>

                    <span>{t("go_to_page")}</span>
                    <input
                        type="number"
                        defaultValue={table.getState().pagination.pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value
                                ? Number(e.target.value) - 1
                                : 0;
                            table.setPageIndex(page);
                        }}
                        className="w-16 border rounded px-2 py-1"
                    />

                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) =>
                            table.setPageSize(Number(e.target.value))
                        }
                        className="border rounded px-2 py-1"
                    >
                        {[5, 10, 20].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {t("show")} {pageSize}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
});
